import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Fragment, useEffect, useState, useMemo } from "react";
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from "react-loading-overlay-ts";
import useAuth from "../hooks/useAuth";
import Config from "../models/Config";
import Cache from "../models/Cache";
import { ClassResults } from "../models/StandingResults";
import { StandingResult } from "../models/StandingResult";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

function StandingsPage() {
  const navigate = useNavigate();
  const user = useAuth();
  const [classResults, setClassResults] = useState<ClassResults[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [classes, setClasses] = useState<string[]>([]);

  useEffect(() => {
    document.title = `${Config.TAB_PREFIX}Standings`;
  }, [])

  const getClassResults = useMemo(() => {
    const fetchData = async (className: string) => {
      setIsLoading(true);
      try {
        let data;
        let jsonData;
        data = await fetch(`${Config.SHEETS_URL}/values/${encodeURIComponent("Standings!G2:G10")}?key=${Config.SHEETS_KEY}`, {
          method: 'GET'
        });
        jsonData = await data.json();
        const classes = jsonData.values.map((classPayload: any) => classPayload[0]);
        setClasses(classes);
        Cache.ClassesList.data = classes;

        data = await fetch(`${Config.SHEETS_URL}/values/${encodeURIComponent("Standings!A2:D500")}?key=${Config.SHEETS_KEY}`, {
          method: 'GET'
        });
        jsonData = await data.json();
        const classResults = jsonData.values.reduce(
          (acc: ClassResults[], standingPayload: any) => {
            const result = new StandingResult(standingPayload);
            const classGroup = acc.find((d) => d.class === result.class);
            if (classGroup) {
              if (!classGroup.results) {
                classGroup.results = [];
              }
              classGroup.results.push(result);
            } else {
              acc.push(new ClassResults(result.class, [result]));
            }
            return acc;
          },
          [] as ClassResults[],
        );
        const filteredResults = classResults.filter((classResult: ClassResults) => classResult.class === className);
        setClassResults(filteredResults);
        Cache.StandingResultPage.set(className, { data: filteredResults, timestamp: Date.now() });
      } catch (error) {
        // Handle error
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
  
    return async (className: string) => {
      if (Cache.StandingResultPage.has(className)) {
        setClassResults(Cache.StandingResultPage.get(className)!.data);
        setClasses(Cache.ClassesList.data);
      } else {
        fetchData(className);
      }
    };
  }, [user]);
  
  // useEffect hook to periodically invalidate cache after 15 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      for (const [classId, cachedData] of Cache.StandingResultPage.entries()) {
        if (Date.now() - cachedData.timestamp >= Config.CACHE_EXPIRATION_TIME) {
          Cache.StandingResultPage.delete(classId);
        }
      }
    }, Config.CACHE_EXPIRATION_TIME);
  
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    getClassResults('Open');
  }, [getClassResults]);


  const handleChange = (event: SelectChangeEvent) => {
    getClassResults(event.target.value);
  };

  return (
    <LoadingOverlay active={isLoading} className="loading-overlay">
      <>
        <Container
          maxWidth="sm"
          sx={{ marginBottom: "60px" }}
          className="page-header"
        >
          <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
            Back
          </Button>
          {!classResults && !isLoading && (
            <div className="center-text">
              <h2>No results available yet.</h2>
            </div>
          )}
          <Stack spacing={2}>
            {classResults &&
              classResults.map((classResult) => {
                return (
                  <Fragment key={classResult.class}>
                      <div>
                        <div>
                          <InputLabel id="demo-simple-select-label">Class Name</InputLabel>
                          <Select
                            value={classResult.class.toString()}
                            label="Class"
                            onChange={handleChange}
                            sx={{ width: "100%",
                            marginBottom: "10px"
                            }}
                          >
                            {classes.map((key: string) => {
                              if (key) {
                                return (
                                  <MenuItem key={key} value={key}>
                                    {key}
                                  </MenuItem>
                                );
                              }
                            }, [])}
                          </Select>
                        </div>
                        <div className="card-row--primary show-result-division-name">
                          {classResult.className.toUpperCase()}
                        </div>
                        {/* Header Row (Flex) */}
                        <div style={{ display: 'flex', fontWeight: 'bold', marginBottom: 8, marginTop: 8, padding: '4px 0' }}>
                          <div style={{ flex: '1 0 40px', minWidth: 40 }}>#</div>
                          <div style={{ flex: '4 0 120px', minWidth: 80 }}>Name</div>
                          <div style={{ flex: '4 0 120px', minWidth: 80 }}>Horse</div>
                          <div style={{ flex: '2 0 60px', minWidth: 50 }}>Points</div>
                        </div>
                        {classResult.finalResults &&
                          classResult.finalResults.map((result) => (
                            <div
                              key={result.name + result.horse}
                              className="show-result-row"
                              style={{ display: 'flex', alignItems: 'center', marginBottom: 4, padding: '4px 0' }}
                            >
                              <div style={{ flex: '1 0 40px', minWidth: 40 }}>{result.position}</div>
                              <div style={{ flex: '4 0 120px', minWidth: 80 }}>{result.name}</div>
                              <div style={{ flex: '4 0 120px', minWidth: 80 }}>{result.horse}</div>
                              <div style={{ flex: '2 0 60px', minWidth: 50 }}>{result.points}</div>
                            </div>
                          ))}
                      </div>
                    </Fragment>
                  );
                })}
            </Stack>
          </Container>
        </>
    </LoadingOverlay>
  );
}
export default StandingsPage;
